import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Paper, Chip, IconButton, Tooltip, CircularProgress, Tabs, Tab, Stack, Divider,
  Alert, LinearProgress, Avatar, useMediaQuery, Grid, Fade, AlertTitle
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Code as CodeIcon, Visibility as VisibilityIcon, Upload as UploadIcon,
  ContentCopy as CopyIcon, AutoAwesome as AutoIcon, Save as SaveIcon,
  Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon,
  DragIndicator as DragIcon, Person as PersonIcon, Business as BusinessIcon,
  CalendarToday as CalendarIcon, Work as WorkIcon, Email as EmailIcon,
  Description as DescriptionIcon, Group as TeamIcon, Security as SecurityIcon,
  AccountCircle as UserIcon, Badge as BadgeIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, Info as InfoIcon
} from '@mui/icons-material';
import client from "../../../../API";

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW PANEL
// ═══════════════════════════════════════════════════════════════════
const ContractPreviewPanel = memo(({ 
  formData, 
  previewHtml, 
  signatureSpots, 
  insertedVariables,
  variables 
}) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Preview Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: formData.is_mandatory ? 'error.main' : 'primary.main',
              }}
            >
              <DescriptionIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {formData.name || 'Untitled Contract'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.contract_type === 'simple' ? 'Simple Agreement' : 'Complex Signature Contract'}
              </Typography>
            </Box>
          </Stack>
          
          <Stack direction="row" spacing={0.5}>
            {formData.is_mandatory && (
              <Chip 
                icon={<SecurityIcon />} 
                label="Mandatory" 
                size="small" 
                color="error"
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            )}
            <Chip 
              icon={<VisibilityIcon />} 
              label="Live Preview" 
              size="small"
              sx={{ height: 24, fontSize: '0.7rem' }}
            />
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Variables Used
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {[...new Set(insertedVariables.map(v => v.id))].length}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Signature Spots
              </Typography>
              <Typography variant="h6" fontWeight={700} color="secondary.main">
                {formData.contract_type === 'simple' ? '0' : signatureSpots.length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {formData.expires_after_days && (
          <Alert severity="info" sx={{ mt: 1.5 }} icon={<CalendarIcon />}>
            <Typography variant="caption">
              Expires {formData.expires_after_days} days after assignment
            </Typography>
          </Alert>
        )}

        {formData.requires_resignation_on_update && (
          <Alert severity="warning" sx={{ mt: 1 }} icon={<WarningIcon />}>
            <Typography variant="caption">
              Requires re-signing when template is updated
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Preview Content */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 3,
          bgcolor: '#f5f5f5',
        }}
      >
        <Paper 
          elevation={3}
          sx={{ 
            minHeight: '100%',
            backgroundColor: 'white',
            p: 3,
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </Paper>
      </Box>

      {/* Variable Reference */}
      {insertedVariables.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            USED VARIABLES
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {[...new Set(insertedVariables.map(v => v.id))].map((id) => {
              const varInfo = variables.find(v => v.id === id);
              return varInfo ? (
                <Chip
                  key={id}
                  size="small"
                  label={varInfo.label}
                  sx={{
                    backgroundColor: `${varInfo.color}20`,
                    color: varInfo.color,
                    fontSize: '10px',
                    height: '22px'
                  }}
                />
              ) : null;
            })}
          </Stack>
        </Paper>
      )}
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const EnhancedContractTemplateDialog = ({ open, template, onClose, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    js_content: '',
    expires_after_days: '7',
    requires_resignation_on_update: true,
    contract_type: 'simple',
    is_mandatory: true
  });
  
  const [editorMode, setEditorMode] = useState(0);
  const [plainText, setPlainText] = useState('');
  const [signatureSpots, setSignatureSpots] = useState([]);
  const [insertedVariables, setInsertedVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showVariablePalette, setShowVariablePalette] = useState(true);
  const [draggedVar, setDraggedVar] = useState(null);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const variables = [
    { id: 'CONTRACT_TITLE', label: 'Contract Title', icon: DescriptionIcon, color: '#2196F3', category: 'contract' },
    
    // Company variables
    { id: 'COMPANY_NAME', label: 'Company Name', icon: BusinessIcon, color: '#4CAF50', category: 'company' },
    { id: 'COMPANY_LEGAL_NAME', label: 'Company Legal Name', icon: BusinessIcon, color: '#66BB6A', category: 'company' },
    { id: 'COMPANY_LEGAL_ENTITY', label: 'Company Legal Entity', icon: BusinessIcon, color: '#81C784', category: 'company' },
    { id: 'COMPANY_AP_EMAIL', label: 'Company AP Email', icon: EmailIcon, color: '#4DB6AC', category: 'company' },
    { id: 'COMPANY_AP_CONTACT', label: 'Company AP Contact', icon: PersonIcon, color: '#4DD0E1', category: 'company' },
    { id: 'COMPANY_AP_PHONE', label: 'Company AP Phone', icon: BadgeIcon, color: '#4FC3F7', category: 'company' },
    { id: 'COMPANY_SUPERVISOR', label: 'Company Supervisor', icon: PersonIcon, color: '#29B6F6', category: 'company' },
    
    // Team variables
    { id: 'TEAM_NAME', label: 'Team Name', icon: TeamIcon, color: '#9C27B0', category: 'team' },
    { id: 'TEAM_LEADER', label: 'Team Leader', icon: PersonIcon, color: '#AB47BC', category: 'team' },
    { id: 'TEAM_BUDGET', label: 'Team Budget', icon: WorkIcon, color: '#BA68C8', category: 'team' },
    { id: 'TEAM_AVAILABLE_CREDIT', label: 'Team Available Spending Power', icon: WorkIcon, color: '#CE93D8', category: 'team' },
    { id: 'TEAM_DESCRIPTION', label: 'Team Description', icon: DescriptionIcon, color: '#E1BEE7', category: 'team' },
    
    // User/Employee variables
    { id: 'USER_NAME', label: 'User Full Name', icon: PersonIcon, color: '#FF9800', category: 'user' },
    { id: 'USER_FIRST_NAME', label: 'User First Name', icon: UserIcon, color: '#FF5722', category: 'user' },
    { id: 'USER_LAST_NAME', label: 'User Last Name', icon: UserIcon, color: '#F44336', category: 'user' },
    { id: 'USER_EMAIL', label: 'User Email', icon: EmailIcon, color: '#E91E63', category: 'user' },
    { id: 'USER_PHONE', label: 'User Phone', icon: BadgeIcon, color: '#9C27B0', category: 'user' },
    { id: 'USER_LEGAL_NAME', label: 'User Legal Name', icon: SecurityIcon, color: '#673AB7', category: 'user' },
    { id: 'USER_BALANCE', label: 'User Balance', icon: WorkIcon, color: '#3F51B5', category: 'user' },
    
    // Creator variables
    { id: 'CREATOR_NAME', label: 'Creator Name', icon: PersonIcon, color: '#00BCD4', category: 'creator' },
    { id: 'CREATOR_USERNAME', label: 'Creator Username', icon: BadgeIcon, color: '#009688', category: 'creator' },
    { id: 'CREATOR_EMAIL', label: 'Creator Email', icon: EmailIcon, color: '#4DB6AC', category: 'creator' },
    { id: 'CREATOR_PHONE', label: 'Creator Phone', icon: BadgeIcon, color: '#26A69A', category: 'creator' },
    { id: 'CREATOR_PAYOUT_EMAIL', label: 'Creator Payout Email', icon: EmailIcon, color: '#00897B', category: 'creator' },
    { id: 'CREATOR_LEGAL_NAME', label: 'Creator Legal Name', icon: SecurityIcon, color: '#00796B', category: 'creator' },
    { id: 'CREATOR_BALANCE', label: 'Creator Balance', icon: WorkIcon, color: '#00695C', category: 'creator' },
    
    // Date and assignment variables
    { id: 'SIGNATURE_DATE', label: 'Signature Date', icon: CalendarIcon, color: '#607D8B', category: 'system' },
    { id: 'SIGNATURE_DATETIME', label: 'Signature DateTime', icon: CalendarIcon, color: '#78909C', category: 'system' },
    { id: 'CURRENT_DATE', label: 'Current Date', icon: CalendarIcon, color: '#90A4AE', category: 'system' },
    { id: 'DUE_DATE', label: 'Due Date', icon: CalendarIcon, color: '#B0BEC5', category: 'system' },
    { id: 'ASSIGNMENT_MESSAGE', label: 'Assignment Message', icon: DescriptionIcon, color: '#CFD8DC', category: 'system' },
    
    // Assigned by variables
    { id: 'ASSIGNED_BY_NAME', label: 'Assigned By Name', icon: PersonIcon, color: '#795548', category: 'assignment' },
    { id: 'ASSIGNED_BY_EMAIL', label: 'Assigned By Email', icon: EmailIcon, color: '#8D6E63', category: 'assignment' },
  ];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        js_content: template.contract_body || '',
        expires_after_days: template.expires_after_days || '',
        requires_resignation_on_update: template.requires_resignation_on_update ?? true,
        contract_type: template.contract_type || 'complex',
        is_mandatory: template.is_mandatory ?? true
      });
      
      const extracted = extractTextFromTemplate(template.contract_body || '');
      setPlainText(extracted.text);
      setInsertedVariables(extracted.variables);
      extractSignatureSpots(template.contract_body || '');
    } else {
      setPlainText(getDefaultPlainText());
    }
  }, [template]);

  const getDefaultPlainText = () => {
    return `EMPLOYMENT AGREEMENT

This Employment Agreement is entered into between {{COMPANY_NAME}} and {{USER_NAME}}.

Employee: {{USER_NAME}}
Email: {{USER_EMAIL}}
Company: {{COMPANY_NAME}}
Start Date: {{SIGNATURE_DATE}}

Terms and Conditions:

1. The employee agrees to perform duties as assigned by the company.
2. The employee will receive compensation as agreed upon.
3. This agreement is effective as of the start date mentioned above.

By signing below, both parties agree to the terms outlined in this agreement.`;
  };

  const extractTextFromTemplate = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || '';
    
    const variableMatches = [...text.matchAll(/\{\{([^}]+)\}\}/g)];
    const variables = variableMatches.map(match => ({
      id: match[1],
      position: match.index
    }));
    
    return { text, variables };
  };

  const extractSignatureSpots = (content) => {
    const spots = [];
    const regex = /\{\{SIGNATURE_SPOT:([^:]+):([^}]+)\}\}/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      spots.push({
        id: match[2],
        label: match[1],
        type: 'signature'
      });
    }
    setSignatureSpots(spots);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        setPlainText(text);
        setInsertedVariables([]);
        setLoading(false);
      };
      reader.readAsText(file);
    } catch (error) {
      alert('Failed to read file. Please try again.');
      setLoading(false);
    }
  };

  const insertVariable = useCallback((variableId) => {
    const editor = editorRef.current;
    if (!editor) return;

    const textarea = editor.querySelector('textarea');
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = plainText.substring(0, cursorPos);
    const textAfter = plainText.substring(cursorPos);
    const variableText = `{{${variableId}}}`;
    
    const newText = textBefore + variableText + textAfter;
    setPlainText(newText);
    
    setInsertedVariables(prev => [...prev, { id: variableId, position: cursorPos }]);
    
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = cursorPos + variableText.length;
      textarea.focus();
    }, 0);
  }, [plainText]);

  const addSignatureSpot = useCallback(() => {
    const label = prompt('Enter signature label (e.g., "Employee Signature"):', 'Employee Signature');
    if (!label) return;
    
    const spotId = `spot_${Date.now()}`;
    const spotText = `\n\n{{SIGNATURE_SPOT:${label}:${spotId}}}`;
    
    setPlainText(prev => prev + spotText);
    setSignatureSpots(prev => [...prev, { label, id: spotId }]);
  }, []);

  const convertToHtml = useCallback((text) => {
    const paragraphs = text.split(/\n\n+/);
    
    let html = `<div className="contract-document" style={{fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '40px', lineHeight: '1.6'}}>
  <div className="header" style={{textAlign: 'center', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px'}}>
    <h1 style={{color: '#333', marginBottom: '10px'}}>{{CONTRACT_TITLE}}</h1>
  </div>

  <div className="content" style={{marginBottom: '40px'}}>
`;

    paragraphs.forEach(para => {
      const trimmed = para.trim();
      if (!trimmed) return;
      
      if (trimmed.length < 60 && (trimmed === trimmed.toUpperCase() || trimmed.endsWith(':'))) {
        html += `    <h3 style={{marginTop: '20px', marginBottom: '10px', color: '#333'}}>${trimmed}</h3>\n`;
      } else if (/^\d+\./.test(trimmed)) {
        const items = trimmed.split(/\n(?=\d+\.)/);
        html += '    <ol style={{marginLeft: "20px", marginBottom: "15px"}}>\n';
        items.forEach(item => {
          const cleaned = item.replace(/^\d+\.\s*/, '').trim();
          html += `      <li style={{marginBottom: "8px"}}>${cleaned}</li>\n`;
        });
        html += '    </ol>\n';
      } else if (/^[-•*]/.test(trimmed)) {
        const items = trimmed.split(/\n(?=[-•*])/);
        html += '    <ul style={{marginLeft: "20px", marginBottom: "15px"}}>\n';
        items.forEach(item => {
          const cleaned = item.replace(/^[-•*]\s*/, '').trim();
          html += `      <li style={{marginBottom: "8px"}}>${cleaned}</li>\n`;
        });
        html += '    </ul>\n';
      } else {
        html += `    <p style={{marginBottom: '15px'}}>${trimmed}</p>\n`;
      }
    });

    html += `  </div>

  <div style={{margin: '30px 0', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
    <strong>Company:</strong> {{COMPANY_NAME}}<br/>
    <strong>User:</strong> {{USER_NAME}}<br/>
    <strong>Email:</strong> {{USER_EMAIL}}<br/>
    <strong>Date:</strong> {{SIGNATURE_DATE}}
  </div>
`;

    if (formData.contract_type === 'simple') {
      html += `
  <div className="agreement-section" style={{marginTop: '40px', textAlign: 'center', padding: '20px', backgroundColor: '#e3f2fd', borderRadius: '5px'}}>
    <p style={{marginBottom: '15px', fontWeight: 'bold', fontSize: '16px'}}>By proceeding, you agree to all terms above.</p>
    {{SIMPLE_AGREEMENT_BUTTON}}
  </div>
`;
    } else if (signatureSpots.length > 0) {
      html += `
  <div className="signature-section" style={{marginTop: '60px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
`;
      signatureSpots.forEach(spot => {
        html += `    {{SIGNATURE_SPOT:${spot.label}:${spot.id}}}\n`;
      });
      html += '  </div>\n';
    }

    html += '</div>';
    return html;
  }, [formData.contract_type, signatureSpots]);

  const generatePreview = useCallback(() => {
    let content = editorMode === 0 ? convertToHtml(plainText) : formData.js_content;
    
    const sampleData = {
      'CONTRACT_TITLE': formData.name || 'Untitled Contract',
      'COMPANY_NAME': 'Acme Corporation',
      'TEAM_NAME': 'Engineering Team',
      'USER_NAME': 'John Doe',
      'USER_FIRST_NAME': 'John',
      'USER_LAST_NAME': 'Doe',
      'USER_EMAIL': 'john.doe@acme.com',
      'USER_PHONE': '+1 (555) 123-4567',
      'CREATOR_NAME': 'Jane Creator',
      'CREATOR_USERNAME': 'janecreator',
      'CREATOR_EMAIL': 'jane@creators.com',
      'SIGNATURE_DATE': new Date().toLocaleDateString(),
      'SIGNATURE_DATETIME': new Date().toLocaleString(),
      'DUE_DATE': 'N/A',
      'ASSIGNMENT_MESSAGE': 'Please review and sign this agreement.'
    };

    Object.entries(sampleData).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), 
        `<span style="background-color: #fef3c7; padding: 2px 8px; border-radius: 4px; font-weight: 500; border: 1px solid #fbbf24;">${value}</span>`);
    });

    content = content.replace(/\{\{SIGNATURE_SPOT:([^:]+):([^}]+)\}\}/g, (match, label) => {
      return `<div style="border: 2px dashed #3b82f6; padding: 24px; text-align: center; margin: 16px 0; border-radius: 8px; background-color: #f8fafc;">
        <div style="color: #64748b; font-size: 13px; margin-bottom: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">${label}</div>
        <div style="height: 50px; border-bottom: 2px solid #1e293b; margin: 12px auto; max-width: 300px;"></div>
        <div style="font-size: 11px; color: #94a3b8; margin-top: 8px;">Signature</div>
        <div style="font-size: 11px; color: #94a3b8; margin-top: 4px;">Date: ___________</div>
      </div>`;
    });

    content = content.replace(/\{\{SIMPLE_AGREEMENT_BUTTON\}\}/g, 
      `<button style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 14px 32px; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
        ✓ I Agree to These Terms
      </button>`
    );

    return content;
  }, [editorMode, convertToHtml, plainText, formData.js_content, formData.name]);

  const handleSubmit = async () => {
    const { name, expires_after_days } = formData;

    if (!name.trim()) {
      alert('Template Name is required.');
      return;
    }

    const finalContent = editorMode === 0 ? convertToHtml(plainText) : formData.js_content;

    if (!finalContent.trim()) {
      alert('Contract Content is required.');
      return;
    }

    if (expires_after_days !== '') {
      const expiresDaysNum = parseInt(expires_after_days, 10);
      if (isNaN(expiresDaysNum) || expiresDaysNum <= 0) {
        alert('Expires After Days must be a positive integer or left empty.');
        return;
      }
    }

    try {
      setLoading(true);

      const dataToSend = {
        name: formData.name,
        description: formData.description,
        js_content: finalContent,
        contract_body: finalContent,
        contract_type: formData.contract_type,
        expires_after_days: formData.expires_after_days === '' ? null : parseInt(formData.expires_after_days, 10),
        requires_resignation_on_update: formData.requires_resignation_on_update,
        is_mandatory: formData.is_mandatory,
        metadata: {
          contract_type: formData.contract_type,
          signature_spots: signatureSpots,
          variables: insertedVariables
        }
      };

      if (template) {
        await client.contracts.updateTemplate(template.id, dataToSend);
      } else {
        await client.contracts.createTemplate(dataToSend);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const variablesByCategory = useMemo(() => {
    return variables.reduce((acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    }, {});
  }, [variables]);

  const categoryLabels = {
    contract: 'Contract Info',
    company: 'Company Info',
    team: 'Team Info',
    user: 'User/Employee Info',
    creator: 'Creator Info',
    system: 'System Variables',
    assignment: 'Assignment Info'
  };

  const previewHtml = useMemo(() => generatePreview(), [generatePreview]);

  if (!open) return null;

  // ═══════════════════════════════════════════════════════════════════
  // MOBILE VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: { 
            m: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            height: '100vh',
          }
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: theme.shadows[4],
            pt: 'max(16px, env(safe-area-inset-top, 0px))',
          }}
        >
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                  {template ? 'Edit' : 'Create'} Contract
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {formData.name || 'Configure your contract template'}
                </Typography>
              </Box>
              
              <IconButton 
                onClick={onClose}
                size="small"
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <Tabs 
              value={editorMode} 
              onChange={(e, v) => setEditorMode(v)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': { 
                  color: 'rgba(255,255,255,0.7)',
                  minHeight: 42,
                  fontSize: '0.875rem'
                },
                '& .Mui-selected': { color: 'white' },
                '& .MuiTabs-indicator': { bgcolor: 'white', height: 3 }
              }}
            >
              <Tab icon={<VisibilityIcon />} iconPosition="start" label="Visual" />
              <Tab icon={<CodeIcon />} iconPosition="start" label="Code" />
            </Tabs>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
          {/* Settings Section */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={2}>
              <TextField
                label="Template Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                size="small"
                fullWidth
                required
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              <Stack direction="row" spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Contract Type</InputLabel>
                  <Select
                    value={formData.contract_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                    label="Contract Type"
                  >
                    <MenuItem value="simple">Simple (Click to Agree)</MenuItem>
                    <MenuItem value="complex">Complex (Signatures)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Expires (Days)"
                  type="number"
                  value={formData.expires_after_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, expires_after_days: e.target.value }))}
                  size="small"
                  sx={{ width: 140 }}
                />
              </Stack>

              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requires_resignation_on_update}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_resignation_on_update: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Require re-signing on update
                    </Typography>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                      color="error"
                      size="small"
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <SecurityIcon fontSize="small" color={formData.is_mandatory ? "error" : "disabled"} />
                      <Typography variant="body2">
                        Mandatory (blocks actions)
                      </Typography>
                    </Stack>
                  }
                />
              </Stack>

              {formData.is_mandatory && (
                <Alert severity="warning" icon={<WarningIcon />}>
                  <Typography variant="caption">
                    Users must sign before proceeding
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* Quick Actions */}
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                size="small"
              >
                Upload
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoIcon />}
                onClick={() => {
                  setPlainText(getDefaultPlainText());
                  setInsertedVariables([]);
                }}
                size="small"
              >
                Template
              </Button>
              {formData.contract_type === 'complex' && editorMode === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSignatureSpot}
                  size="small"
                  color="secondary"
                >
                  Signature
                </Button>
              )}
            </Stack>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".txt,.pdf,.doc,.docx"
              onChange={handleFileUpload}
            />
          </Paper>

          {/* Editor */}
          <Box sx={{ height: 'calc(100vh - 420px)', minHeight: 300 }}>
            <Tabs 
              value={editorMode === 0 ? 0 : 1}
              onChange={(e, v) => setEditorMode(v)}
              variant="fullWidth"
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Tab label="Editor" />
              <Tab label="Preview" />
            </Tabs>

            <Box sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
              {editorMode === 0 ? (
                <Box sx={{ p: 2 }}>
                  {showVariablePalette && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        TAP TO INSERT
                      </Typography>
                      {Object.entries(variablesByCategory).map(([category, vars]) => (
                        <Box key={category} sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            {categoryLabels[category]}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {vars.map((variable) => (
                              <Chip
                                key={variable.id}
                                label={variable.label}
                                onClick={() => insertVariable(variable.id)}
                                size="small"
                                sx={{
                                  backgroundColor: `${variable.color}30`,
                                  color: variable.color,
                                  fontSize: '0.7rem',
                                  height: 24,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <TextField
                    ref={editorRef}
                    multiline
                    fullWidth
                    value={plainText}
                    onChange={(e) => setPlainText(e.target.value)}
                    placeholder="Type your contract here..."
                    sx={{
                      '& .MuiInputBase-root': {
                        fontFamily: 'Georgia, serif',
                        fontSize: '14px',
                        lineHeight: '1.8',
                      },
                      '& .MuiInputBase-input': {
                        minHeight: '300px !important'
                      }
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions 
          sx={{ 
            p: 2, 
            gap: 1, 
            bgcolor: 'background.paper',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <Button onClick={onClose} variant="text" size="medium">
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
            sx={{ minWidth: 100 }}
          >
            {loading ? 'Saving...' : (template ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DESKTOP VIEW
  // ═══════════════════════════════════════════════════════════════════
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: 0,
        }
      }}
    >
      {/* Desktop Header */}
      <Box
        sx={{
          height: 80,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {template ? 'Edit' : 'Create'} Contract Template
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formData.name || 'Configure your contract template settings'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Tabs value={editorMode} onChange={(e, v) => setEditorMode(v)}>
            <Tab icon={<VisibilityIcon />} iconPosition="start" label="Visual Editor" />
            <Tab icon={<CodeIcon />} iconPosition="start" label="Code Editor" />
          </Tabs>
          
          <IconButton 
            onClick={onClose}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content - Two Column */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column - Preview */}
        <Box
          sx={{
            width: 420,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <ContractPreviewPanel
            formData={formData}
            previewHtml={previewHtml}
            signatureSpots={signatureSpots}
            insertedVariables={insertedVariables}
            variables={variables}
          />
        </Box>

        {/* Right Column - Editor */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Settings Bar */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Template Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Contract Type</InputLabel>
                  <Select
                    value={formData.contract_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                    label="Contract Type"
                  >
                    <MenuItem value="simple">Simple (Click to Agree)</MenuItem>
                    <MenuItem value="complex">Complex (Signatures)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Expires After (Days)"
                  type="number"
                  value={formData.expires_after_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, expires_after_days: e.target.value }))}
                  size="small"
                  fullWidth
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requires_resignation_on_update}
                        onChange={(e) => setFormData(prev => ({ ...prev, requires_resignation_on_update: e.target.checked }))}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Re-sign on update</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_mandatory}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                        color="error"
                        size="small"
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SecurityIcon fontSize="small" color={formData.is_mandatory ? "error" : "disabled"} />
                        <Typography variant="body2">Mandatory</Typography>
                      </Stack>
                    }
                  />
                </Stack>
              </Grid>
            </Grid>

            {formData.is_mandatory && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                <Typography variant="caption">
                  ⚠️ Mandatory contracts will block users from proceeding until they sign
                </Typography>
              </Alert>
            )}
          </Paper>

          {/* Quick Actions */}
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                size="small"
              >
                Upload Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.readText().then(text => {
                    setPlainText(text);
                    setInsertedVariables([]);
                  });
                }}
                size="small"
              >
                Paste from Clipboard
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoIcon />}
                onClick={() => {
                  setPlainText(getDefaultPlainText());
                  setInsertedVariables([]);
                }}
                size="small"
              >
                Use Template
              </Button>
              {formData.contract_type === 'complex' && editorMode === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSignatureSpot}
                  size="small"
                  color="secondary"
                >
                  Add Signature Spot
                </Button>
              )}
            </Stack>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".txt,.pdf,.doc,.docx"
              onChange={handleFileUpload}
            />
          </Paper>

          {/* Editor Area */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Variable Palette */}
            {showVariablePalette && editorMode === 0 && (
              <Paper 
                elevation={0} 
                sx={{ 
                  width: 300, 
                  p: 2, 
                  overflow: 'auto',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#fafafa'
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    DRAG OR CLICK TO INSERT
                  </Typography>
                  <IconButton size="small" onClick={() => setShowVariablePalette(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                
                {Object.entries(variablesByCategory).map(([category, vars]) => (
                  <Box key={category} sx={{ mb: 2.5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      {categoryLabels[category]}
                    </Typography>
                    <Stack spacing={0.75}>
                      {vars.map((variable) => {
                        const IconComponent = variable.icon;
                        return (
                          <Chip
                            key={variable.id}
                            icon={<IconComponent />}
                            label={variable.label}
                            onClick={() => insertVariable(variable.id)}
                            onDelete={() => {}}
                            deleteIcon={<DragIcon />}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('variable', variable.id);
                              setDraggedVar(variable.id);
                            }}
                            onDragEnd={() => setDraggedVar(null)}
                            sx={{
                              justifyContent: 'flex-start',
                              cursor: 'grab',
                              backgroundColor: 'white',
                              border: `2px solid ${variable.color}30`,
                              color: variable.color,
                              fontWeight: 500,
                              fontSize: '11px',
                              height: '30px',
                              opacity: draggedVar === variable.id ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: `${variable.color}15`,
                                borderColor: variable.color,
                              },
                              '&:active': {
                                cursor: 'grabbing',
                              },
                              '& .MuiChip-deleteIcon': {
                                color: variable.color,
                                opacity: 0.6,
                                '&:hover': { opacity: 1 }
                              }
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                ))}

                {signatureSpots.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      SIGNATURE SPOTS
                    </Typography>
                    <Stack spacing={0.5}>
                      {signatureSpots.map((spot) => (
                        <Chip
                          key={spot.id}
                          icon={<PersonIcon />}
                          label={spot.label}
                          size="small"
                          onDelete={() => {
                            const spotText = `{{SIGNATURE_SPOT:${spot.label}:${spot.id}}}`;
                            setPlainText(prev => prev.replace(spotText, ''));
                            setSignatureSpots(prev => prev.filter(s => s.id !== spot.id));
                          }}
                          deleteIcon={<DeleteIcon />}
                          sx={{
                            justifyContent: 'flex-start',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>
            )}

            {!showVariablePalette && editorMode === 0 && (
              <IconButton
                onClick={() => setShowVariablePalette(true)}
                sx={{
                  position: 'absolute',
                  left: 420,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <DragIcon />
              </IconButton>
            )}

            {/* Text Editor */}
            <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
              {editorMode === 0 ? (
                <TextField
                  ref={editorRef}
                  multiline
                  fullWidth
                  value={plainText}
                  onChange={(e) => setPlainText(e.target.value)}
                  placeholder="Type or paste your contract here... 

Use {{VARIABLE_NAME}} format or click/drag variables from the left panel.

Press Enter twice to create new paragraphs.
Use numbered lists (1. 2. 3.) or bullet points (- or •) for lists."
                  onDrop={(e) => {
                    e.preventDefault();
                    const variableId = e.dataTransfer.getData('variable');
                    if (variableId) {
                      insertVariable(variableId);
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'Georgia, serif',
                      fontSize: '15px',
                      lineHeight: '1.8',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      minHeight: '500px !important'
                    }
                  }}
                />
              ) : (
                <TextField
                  multiline
                  fullWidth
                  value={formData.js_content}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, js_content: e.target.value }));
                    extractSignatureSpots(e.target.value);
                  }}
                  placeholder="Edit HTML/JSX code here..."
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'Consolas, Monaco, monospace',
                      fontSize: '14px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      minHeight: '500px !important'
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons - Sticky */}
          <Paper 
            elevation={3}
            sx={{ 
              p: 2.5, 
              borderRadius: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={onClose} size="large" variant="text">
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ minWidth: 160 }}
              >
                {loading ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EnhancedContractTemplateDialog;